/**
 * Story shape estimation — the ONE formula for how many blocks a story of a
 * given target duration has. Used by the cost estimate (wizard review), the
 * engine bundle inputs (story-generation blockCount) and mirrors the legacy
 * TextGenerator maths, so the forecast a user sees matches what is generated.
 */

/** Sentences per block by age/duration (rhythm: toddlers get short blocks). */
function sentencesPerBlock(durationMinutes: number, ageGroup?: string): number {
  if (ageGroup === '1-3') return 2;
  return durationMinutes >= 7 ? 5 : 3;
}

/** Blocks per minute scales inversely with block length. */
function blocksPerMinute(spb: number): number {
  if (spb <= 2) return 6;
  if (spb === 5) return 4;
  return 5;
}

export function estimateStoryBlockCount(durationMinutes: number, ageGroup?: string): number {
  const spb = sentencesPerBlock(durationMinutes, ageGroup);
  return Math.max(5, Math.round(durationMinutes * blocksPerMinute(spb)));
}

const TTS_WORDS_PER_MINUTE: Record<string, number> = {
  ru: 130, en: 150, cs: 130, uk: 130, de: 130, fr: 140,
};
const AVG_CHARS_PER_WORD = 6;

/** Target block length per age (mirrors the worker's text.to_blocks
 *  _AGE_TARGET_CHARS). One block = one page of the book. */
const AGE_TARGET_CHARS: Record<string, number> = {
  '1-3': 180, '3-5': 250, '5-7': 350, '7-10': 500, '10+': 650,
};

/** Historic name, kept for the from-text slicer's own reading. */
const FROM_TEXT_TARGET_CHARS = AGE_TARGET_CHARS;

// ─── Book size ────────────────────────────────────────────────────────
//
// PAGES are what a parent chooses and what the pipeline is told. Minutes used
// to be the question, and they were a fiction twice over: no prompt ever sees a
// duration (the writer is told `exactly {{ inputs.blockCount }} blocks`), and
// the control was only drawn for orders that included narration — so a book
// silently took the 7-minute default and came out at 28 pages. A page is also
// the unit that costs money, so the thing chosen and the thing paid for are now
// the same thing.
//
// THE SIZES DEPEND ON THE AGE, because a page does. A page holds one target
// length per age (AGE_TARGET_CHARS above: 180 characters at two years old, 650
// at eleven), so the same page count is a different book — and a very
// different sitting — at either end. Sized so that the LARGEST book of every
// age still reads and LISTENS in one go: at ru pace (130 wpm, ≈780 characters
// a minute) the big size lands near 3 minutes for a toddler, 11 for a
// five-year-old and 18 for the oldest. A flat 24 pages would have been 3
// minutes of nothing at eleven and half an hour of squirming at two.
const BOOK_SIZES_BY_AGE: Record<string, { small: number; medium: number; large: number }> = {
  '1-3':  { small: 6,  medium: 10, large: 14 },
  '3-5':  { small: 8,  medium: 12, large: 18 },
  '5-7':  { small: 10, medium: 16, large: 24 },
  '7-10': { small: 10, medium: 16, large: 24 },
  '10+':  { small: 10, medium: 16, large: 22 },
};

export type BookSize = 'small' | 'medium' | 'large';

/** The sizes offered at this age (the 5-7 row is the default everywhere else). */
export function bookSizesFor(ageGroup?: string): Record<BookSize, number> {
  return BOOK_SIZES_BY_AGE[ageGroup ?? ''] ?? BOOK_SIZES_BY_AGE['5-7'];
}

/** The default-age row, for the places that count in books rather than offer a
 *  choice (the allowance meter's "small book" unit). */
export const BOOK_SIZES = BOOK_SIZES_BY_AGE['5-7'];

export const DEFAULT_BOOK_SIZE: BookSize = 'medium';

/** What a hand-typed page count is allowed to be for a GENERATED story: never
 *  fewer than four pages (below that it is a card, not a book) and never more
 *  than the large size of that age. */
export function pageRangeFor(ageGroup?: string): { min: number; max: number } {
  return { min: 4, max: bookSizesFor(ageGroup).large };
}

/** The size whose page count this is at this age, when it is one of them. */
export function bookSizeOf(pages: number, ageGroup?: string): BookSize | null {
  const sizes = bookSizesFor(ageGroup);
  return (Object.keys(sizes) as BookSize[]).find((k) => sizes[k] === pages) ?? null;
}

export function clampPageCount(pages: number, ageGroup?: string): number {
  const { min, max } = pageRangeFor(ageGroup);
  if (!Number.isFinite(pages)) return bookSizesFor(ageGroup)[DEFAULT_BOOK_SIZE];
  return Math.max(min, Math.min(max, Math.round(pages)));
}

/**
 * The page count after the age changed.
 *
 * A parent who chose "medium" and then corrected the child's age meant medium
 * for the new age, not the old number of pages — so a count that WAS a named
 * size becomes the same name at the new age. A hand-typed number is kept, only
 * clamped into what the new age allows.
 */
export function resizeForAge(pages: number, fromAge?: string, toAge?: string): number {
  const named = bookSizeOf(pages, fromAge);
  if (named) return bookSizesFor(toAge)[named];
  return clampPageCount(pages, toAge);
}

/** Characters of story a page count means at this age — the inverse of the
 *  slicer's target length, and the basis of every TTS estimate. */
export function charsForBlocks(blockCount: number, ageGroup?: string): number {
  const target = AGE_TARGET_CHARS[ageGroup ?? ''] ?? 350;
  return Math.max(1, Math.round(blockCount * target));
}

/** Reading time a page count comes to — shown next to the choice, never asked
 *  for. Same words-per-minute table the cost forecast uses. */
export function minutesForBlocks(blockCount: number, ageGroup?: string, language?: string): number {
  const wpm = TTS_WORDS_PER_MINUTE[language ?? 'ru'] ?? 130;
  const words = charsForBlocks(blockCount, ageGroup) / AVG_CHARS_PER_WORD;
  return Math.max(1, Math.round(words / wpm));
}

// ─── From-text: no size rules at all ──────────────────────────────────
//
// A story the parent brought is already the length it is. The age targets above
// are about how much text a CHILD of that age takes on a page, which is a
// decision we make when we write the story; we do not get to make it about
// somebody else's text, and cutting their tale to a toddler's page count would
// be rewriting it. So a pasted text is paged by its own shape — by the model
// doing the intake, for a briefed document, or by the free deterministic
// slicer for an ordinary tale — and the ONLY ceiling is what the model can hold
// in one context.
//
// 90 blocks is that ceiling, not a taste: at the longest page (650 characters)
// it is ≈ 58 000 characters ≈ 20 000 tokens of story, which fits beside the
// instructions in the smallest context window we configure
// (llm.azure.limits.maxContextWindow). The run itself never asks for all of it
// at once — text generation goes in chunks of STORY_CHUNK_SIZE and the
// storyboard in chunks of PAGES_CHUNK_SIZE — so the limit is on the intake
// pass, which does read the whole text.
export const FROM_TEXT_MAX_BLOCKS = 90;

/**
 * Block count for a FROM-TEXT story — from the text itself, never from a
 * duration (there is none).
 *
 * verbatim / translate / recast / incorporate keep the source's shape
 * (≈ chars / target block length). based_on writes a NEW story, but its length
 * still follows the source: a flat 12 turned a 7,000-character text into twelve
 * pages, which is not "по мотивам", it is a summary. It is the same ratio,
 * loosened (a retelling is tighter than the original) and clamped to a picture
 * book's range.
 *
 * Clamped at FROM_TEXT_MAX_BLOCKS — the intake context, not a page budget. The
 * clamp exists at all because the illustration DAG (chunked above 12) must ask
 * for pages that exist: a 7-block tale used to get a 25-page chunked
 * storyboard.
 */
export function estimateFromTextBlockCount(
  text: string,
  ageGroup?: string,
  mode?: string,
): number {
  const target = FROM_TEXT_TARGET_CHARS[ageGroup ?? ''] ?? 350;
  const chars = text?.trim().length ?? 0;
  if (mode === 'based_on') {
    // A retelling keeps the story's SHAPE, not its word count: roughly two
    // source pages per page of the book. It used to stop at 24 — a borrowed
    // 40,000-character tale came back as a summary because of a number chosen
    // for OUR books. The only ceiling is the context one.
    return Math.max(8, Math.min(FROM_TEXT_MAX_BLOCKS, Math.round(chars / (target * 2)) || 8));
  }
  const byLength = Math.round(chars / target);
  const paragraphs = (text ?? '').split(/\n\s*\n/).filter((p) => p.trim()).length;
  return Math.max(3, Math.min(FROM_TEXT_MAX_BLOCKS, Math.max(byLength, Math.min(paragraphs, byLength * 2 || paragraphs))));
}

/**
 * The deterministic slice of a pasted text into blocks — a port of the
 * worker's `text.to_blocks` fallback slicer (portal-workers
 * app/workflows/steps/llm.py `_slice_text`): paragraphs on blank lines (else
 * lines), an over-long paragraph split on sentence ends and regrouped to the
 * age's target, a tiny trailing fragment folded back. The worker may instead
 * ask a model to GROUP paragraphs, which only ever gives fewer pages — so this
 * count is the most pages a pasted text can take, the right number to hold
 * before the order starts.
 */
export function sliceFromTextBlocks(text: string, ageGroup?: string): string[] {
  const target = FROM_TEXT_TARGET_CHARS[ageGroup ?? ''] ?? 350;
  const t = (text ?? '').trim();
  if (!t) return [];
  const blankLine = /\n[ \t\r]*\n/;
  const units = (blankLine.test(t)
    ? t.split(/\n[ \t\r]*\n/).map((p) => p.split('\n').map((l) => l.trim()).filter(Boolean).join(' '))
    : t.split('\n').map((l) => l.trim())
  ).filter(Boolean);

  const sentences = (u: string): string[] => {
    const out: string[] = [];
    const re = /[.!?…]+["»”’')\]]*(?=\s|$)/g;
    let start = 0;
    for (let m = re.exec(u); m; m = re.exec(u)) {
      const chunk = u.slice(start, m.index + m[0].length).trim();
      if (chunk) out.push(chunk);
      start = m.index + m[0].length;
    }
    const tail = u.slice(start).trim();
    if (tail) out.push(tail);
    return out;
  };
  const group = (pieces: string[]): string[] => {
    const blocks: string[] = [];
    let cur = '';
    for (const p of pieces) {
      if (!cur) cur = p;
      else if (cur.length + 1 + p.length <= target) cur = `${cur} ${p}`;
      else { blocks.push(cur); cur = p; }
    }
    if (cur) blocks.push(cur);
    return blocks;
  };
  const mergeTiny = (blocks: string[]): string[] => {
    const out: string[] = [];
    for (const b of blocks) {
      if (out.length && b.length < 60) out[out.length - 1] = `${out[out.length - 1]} ${b}`;
      else out.push(b);
    }
    return out;
  };

  const pieces: string[] = [];
  for (const u of units) {
    if (u.length <= target) pieces.push(u);
    else pieces.push(...mergeTiny(group(sentences(u))));
  }
  return pieces;
}

/**
 * Pages a pasted text will be held for before the order starts: the slicer's
 * count for a text kept as it is (verbatim, translate, recast…), the
 * retelling estimate for `based_on` (a new text, its length follows the
 * source). Clamped to the intake ceiling, as the run is.
 */
export function fromTextPreflightBlockCount(text: string, ageGroup?: string, mode?: string): number {
  if (mode === 'based_on') return estimateFromTextBlockCount(text, ageGroup, mode);
  const n = sliceFromTextBlocks(text, ageGroup).length;
  return Math.max(1, Math.min(FROM_TEXT_MAX_BLOCKS, n));
}

export interface StoryShape {
  blockCount: number;
  totalChars: number;
  avgCharsPerBlock: number;
}

/** Predicted shape for a story that does not exist yet — duration/age/language
 *  driven, mirrors TextGenerator targets. ONE source for forecast AND any
 *  pre-generation reservation maths. */
export function estimateStoryShape(
  durationMinutes: number,
  ageGroup?: string,
  language?: string,
): StoryShape {
  const blockCount = estimateStoryBlockCount(durationMinutes, ageGroup);
  const wpm = TTS_WORDS_PER_MINUTE[language ?? 'ru'] ?? 130;
  const totalWords = Math.round(durationMinutes * wpm * 1.15);
  const totalChars = totalWords * AVG_CHARS_PER_WORD;
  return {
    blockCount,
    totalChars,
    avgCharsPerBlock: Math.max(1, Math.round(totalChars / blockCount)),
  };
}
